// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Tokenizer.tsx
 * @input Uses React, BaseTypeahead, Field, Token
 * @output Exports Tokenizer multi-select typeahead component
 * @position Composed component; forwards DOM ref and exposes focus control via
 *   handleRef
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Tokenizer/index.ts
 * - /apps/storybook/stories/Tokenizer.stories.tsx
 * - /packages/cli/templates/blocks/components/Tokenizer/ (showcase blocks)
 */
import React, { useCallback, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { BaseTypeahead } from "../Typeahead/BaseTypeahead.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { Field, InputClearButton, inputWrapperStyles, inputStatusBorderStyles, inputStatusHoverShadowStyles, inputStatusFocusWithinStyles } from "../Field/index.js";
import { Token } from "../Token/index.js";
import { renderIconSlot } from "../Icon/index.js";
import { OverflowList } from "../OverflowList/index.js";
import { useLayer } from "../Layer/useLayer.js";
import { useTooltip } from "../Tooltip/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, sizeVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// Re-export status types for convenience

// =============================================================================
// Types
// =============================================================================

/**
 * Change metadata for onChange callback.
 */

/**
 * Controls overflow behavior when tokens exceed the available width.
 * - `'none'`: All tokens wrap normally (default).
 * - `'unfocusedInline'`: Shows a single line with "+ N more" when unfocused, expands inline on focus.
 * - `'unfocusedLayer'`: Shows a single line with "+ N more" when unfocused, expands as an overlay on focus.
 */

/**
 * Imperative handle for Tokenizer handleRef.
 */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  wrapper: {
    kVAEAm: "x1n2onr6",
    kwnvtZ: "x1a02dak",
    kOIVth: "xzye2dw",
    kkrTdU: "x1ed109x",
    kZKoxP: "xt7dq6l",
    $$css: true
  },
  wrapperWithTokens: {
    k8WAf4: "x1x7cmac",
    kg3NbH: "xx2oswg",
    khm7nJ: "xajebq8",
    $$css: true
  },
  token: {
    k1xSpc: "x78zum5",
    kmuXW: "x2lah0s",
    $$css: true
  },
  endSection: {
    kVAEAm: "x10l6tqk",
    kt4wiu: "x72tfeb",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    kmuXW: "x2lah0s",
    $$css: true
  },
  inputAtMax: {
    kzqmXN: "xnalus7",
    k7Eaqz: "xeuugli",
    kUk6DE: "x1somy94",
    kmVPX3: "x1717udv",
    kSiTet: "xg01cxk",
    kVAEAm: "x10l6tqk",
    $$css: true
  },
  inputCompact: {
    k7Eaqz: "x1fns5xo",
    kUk6DE: "xfoc8du",
    kzqmXN: "xnalus7",
    kZCmMZ: "x2a0j69",
    $$css: true
  },
  truncatedWrapper: {
    kwnvtZ: "xozqiw3",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  layerPopover: {
    kzqmXN: "x1xgy17j",
    $$css: true
  }
};
const sizeStyles = {
  sm: {
    kAzted: "x1gdqauq",
    $$css: true
  },
  md: {
    kAzted: "x1uogy3y",
    $$css: true
  },
  lg: {
    kAzted: "x1xf4nls",
    $$css: true
  }
};
const endSectionSizeStyles = {
  sm: {
    k87sOh: "x1968x6z",
    k3aq6I: "x1cb1t30",
    $$css: true
  },
  md: {
    k87sOh: "xkmsrut",
    k3aq6I: "x1cb1t30",
    $$css: true
  },
  lg: {
    k87sOh: "x1oysc8t",
    k3aq6I: "x1cb1t30",
    $$css: true
  }
};
const truncatedSizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  }
};
const layerPlaceholderSizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

// Sentinel prefix for creatable items — used to distinguish
// "Create: X" suggestions from real search results.
const CREATABLE_ID_PREFIX = '__xds_create__';

/**
 * Multi-select input with token chips and typeahead search.
 *
 * Composes BaseTypeahead for search and Token for selected items.
 * Tokens render inline before the text input. Selecting an item adds a token
 * and clears the query. Backspace on empty input removes the last token.
 *
 * @example
 * ```
 * const [members, setMembers] = useState<UserItem[]>([]);
 * <Tokenizer
 *   label="Team members"
 *   searchSource={userSource}
 *   value={members}
 *   onChange={(items, change) => {
 *     setMembers(items);
 *     if (change.type === 'add') {
 *       console.log('Added:', change.item.label);
 *     }
 *   }}
 *   placeholder="Search people..."
 * />
 * <Tokenizer
 *   label="Tags"
 *   searchSource={tagSource}
 *   value={tags}
 *   onChange={(items) => setTags(items)}
 *   renderToken={(item, onRemove) => (
 *     <Token
 *       label={item.label}
 *       color={item.auxiliaryData.color}
 *       onRemove={onRemove}
 *     />
 *   )}
 *   maxEntries={5}
 * />
 * ```
 */
export function Tokenizer({
  label,
  isLabelHidden = false,
  description,
  isRequired = false,
  isOptional = false,
  status,
  startIcon,
  labelTooltip,
  searchSource,
  value,
  onChange,
  renderItem,
  renderToken,
  maxEntries,
  placeholder,
  hasEntriesOnFocus,
  maxMenuItems,
  emptySearchResultsText,
  isDisabled = false,
  htmlName,
  disabledMessage,
  hasClear = false,
  endContent,
  hasAutoFocus,
  size: sizeProp,
  tokenOverflowBehavior = 'none',
  debounceMs,
  hasCreate = false,
  onChangeQuery,
  onFocus,
  onBlur,
  width,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ref,
  handleRef
}) {
  const t = useTranslator();
  const size = useSize(sizeProp, 'md');
  const inputId = useId();
  const descriptionId = useId();
  const statusMessageId = useId();
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the input wrapper and the typeahead input stays
  // perceivable via aria-disabled instead of the disabled attribute. Input is
  // blocked by the isDisabled guards in BaseTypeahead and handleWrapperClick.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The wrapper is not naturally focusable; focusin bubbles up from the
    // input, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  useImperativeHandle(handleRef, () => ({
    focus() {
      inputRef.current?.focus();
    },
    blur() {
      inputRef.current?.blur();
    }
  }));

  // Focus-within state for overflow truncation
  const [isFocusedWithin, setIsFocusedWithin] = useState(false);
  const isTruncated = !isFocusedWithin && tokenOverflowBehavior !== 'none' && value.length > 0;

  // Layer for unfocusedLayer mode — promotes expanded content to the top layer
  // so it isn't clipped by ancestor overflow.
  const isLayerMode = tokenOverflowBehavior === 'unfocusedLayer';
  const layer = useLayer({
    mode: 'context'
  });
  const layerContentRef = useRef(null);

  // Anchor the layer to the placeholder element
  const placeholderRef = useCallback(el => {
    if (isLayerMode) {
      layer.ref(el);
    }
  }, [isLayerMode, layer]);

  // For the layer variant, focus can be in either the placeholder or the
  // popover content. We track both to decide when focus has truly left.
  const isFocusInTokenizer = useCallback(target => {
    if (!target) {
      return false;
    }
    if (wrapperRef.current?.contains(target)) {
      return true;
    }
    if (layerContentRef.current?.contains(target)) {
      return true;
    }
    // Also check the popover element itself (the layer wrapper)
    const popoverEl = document.getElementById(layer.id);
    if (popoverEl?.contains(target)) {
      return true;
    }
    return false;
  }, [layer.id]);
  const handleFocusCapture = useCallback(e => {
    const comingFromOutside = !isFocusInTokenizer(e.relatedTarget);
    setIsFocusedWithin(true);
    if (isLayerMode) {
      layer.show();
    }
    if (comingFromOutside) {
      onFocus?.(e);
      // Redirect to the input so the user doesn't have to tab through
      // every token remove button.
      if (e.target !== inputRef.current) {
        inputRef.current?.focus();
      }
    }
  }, [isLayerMode, layer, isFocusInTokenizer, onFocus]);
  const handleBlurCapture = useCallback(e => {
    if (!isFocusInTokenizer(e.relatedTarget)) {
      setIsFocusedWithin(false);
      onBlur?.(e);
      if (isLayerMode) {
        layer.hide();
      }
    }
  }, [isLayerMode, layer, isFocusInTokenizer, onBlur]);
  const isAtMax = maxEntries != null && value.length >= maxEntries;

  // Filter out already-selected items from search results
  const selectedIds = useMemo(() => new Set(value.map(item => item.id)), [value]);
  const filteredSource = useMemo(() => ({
    search: async query => {
      const results = await searchSource.search(query);
      const filtered = results.filter(item => !selectedIds.has(item.id));

      // Append a "Create: X" synthetic item when hasCreate is true,
      // the user has typed something, and it doesn't exactly match an
      // existing result.
      if (hasCreate && query.trim()) {
        const trimmed = query.trim();
        const alreadyExists = selectedIds.has(trimmed) || filtered.some(item => item.label.toLowerCase() === trimmed.toLowerCase());
        if (!alreadyExists) {
          const creatableItem = {
            id: `${CREATABLE_ID_PREFIX}${trimmed}`,
            label: `Create "${trimmed}"`,
            auxiliaryData: {
              __createdValue: trimmed
            }
          };
          filtered.push(creatableItem);
        }
      }
      return filtered;
    },
    bootstrap: async () => {
      const results = await searchSource.bootstrap();
      return results.filter(item => !selectedIds.has(item.id));
    }
  }), [searchSource, selectedIds, hasCreate]);
  const emptySource = useMemo(() => ({
    search: async () => [],
    bootstrap: async () => []
  }), []);

  // Handle adding an item — detect creatable synthetic items
  const handleAdd = useCallback(item => {
    if (!item) {
      return;
    }
    if (isAtMax) {
      return;
    }

    // Detect "Create: X" synthetic items from the creatable source
    if (hasCreate && typeof item.id === 'string' && item.id.startsWith(CREATABLE_ID_PREFIX)) {
      const createdValue = item.id.slice(CREATABLE_ID_PREFIX.length);
      if (selectedIds.has(createdValue)) {
        return;
      }
      const base = {
        id: createdValue,
        label: createdValue
      };
      const realItem = base;
      const newItems = [...value, realItem];
      onChange(newItems, {
        item: realItem,
        type: 'create'
      });
      return;
    }
    if (selectedIds.has(item.id)) {
      return;
    }
    const newItems = [...value, item];
    onChange(newItems, {
      item,
      type: 'add'
    });
  }, [value, onChange, isAtMax, selectedIds, hasCreate]);

  // Handle removing an item
  const handleRemove = useCallback(item => {
    const newItems = value.filter(v => v.id !== item.id);
    onChange(newItems, {
      item,
      type: 'remove'
    });
    inputRef.current?.focus();
  }, [value, onChange]);

  // Handle clearing all items
  const handleClearAll = useCallback(() => {
    if (value.length === 0) {
      return;
    }
    // Report the last item as removed (convention)
    const lastItem = value[value.length - 1];
    onChange([], {
      item: lastItem,
      type: 'remove'
    });
    inputRef.current?.focus();
  }, [value, onChange]);

  // Handle backspace on empty input — remove last token
  const handleKeyDown = useCallback(e => {
    if (e.key === 'Backspace' && e.currentTarget.value === '' && value.length > 0) {
      e.preventDefault();
      const lastItem = value[value.length - 1];
      handleRemove(lastItem);
    }
  }, [value, handleRemove]);

  // Click wrapper to focus input
  const handleWrapperClick = useCallback(() => {
    if (!isDisabled) {
      if (isLayerMode) {
        // The input always lives in the popover. Show it and focus.
        layer.show();
        setIsFocusedWithin(true);
        // The input is already mounted in the popover (not conditional),
        // so we can focus it directly.
        inputRef.current?.focus();
      } else {
        inputRef.current?.focus();
      }
    }
  }, [isDisabled, isLayerMode, layer]);
  const ariaDescribedBy = [description ? descriptionId : null, status?.message ? statusMessageId : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null].filter(Boolean).join(' ') || undefined;
  const sizeStyle = sizeStyles[size];

  // Render tokens
  const tokens = value.map(item => {
    const onRemoveItem = () => handleRemove(item);
    if (renderToken) {
      return /*#__PURE__*/_jsx("span", {
        ...{
          className: "x78zum5 x2lah0s"
        },
        children: renderToken(item, onRemoveItem)
      }, item.id);
    }
    return /*#__PURE__*/_jsx(Token, {
      label: item.label,
      size: size,
      onRemove: isDisabled ? undefined : onRemoveItem,
      isDisabled: isDisabled,
      xstyle: styles.token
    }, item.id);
  });

  // Self-authored position styles (positioning: 'custom' below): explicit
  // anchor() insets pin the expanded layer over the field itself.
  // `left` is physical, so this popover does not yet mirror in RTL —
  // known follow-up from #3389.
  const popoverOverrideStyle = {
    top: 'anchor(top)',
    left: 'anchor(start)'
  };
  const wrapperContent = /*#__PURE__*/_jsxs("div", {
    ref: el => {
      wrapperRef.current = el;
      // Anchor + hover/focus listeners for the disabled-message tooltip.
      // Handlers are gated internally by isEnabled, so attaching
      // unconditionally is safe.
      disabledMessageTooltip.ref(el);
    },
    role: "group",
    "aria-label": label,
    onClick: handleWrapperClick,
    onFocusCapture: handleFocusCapture,
    onBlurCapture: handleBlurCapture,
    "data-testid": testId,
    ...mergeProps(themeProps('tokenizer', {
      size,
      status: status?.type
    }), stylex.props(inputWrapperStyles.base, styles.wrapper, value.length > 0 && styles.wrapperWithTokens, isTruncated ? truncatedSizeStyles[size] : sizeStyle, isTruncated && styles.truncatedWrapper, isDisabled && inputWrapperStyles.disabled, status && inputStatusBorderStyles[status.type], status && inputStatusHoverShadowStyles[status.type], status && inputStatusFocusWithinStyles[status.type])),
    children: [startIcon && /*#__PURE__*/_jsx("span", {
      ...{
        0: {},
        1: {
          className: "x1xrm450"
        }
      }[!!(value.length > 0) << 0],
      children: renderIconSlot(startIcon, {
        size: 'sm',
        color: 'secondary'
      })
    }), isTruncated ? /*#__PURE__*/_jsx(OverflowList, {
      gap: 1,
      behavior: "observeParent",
      overflowRenderer: items => /*#__PURE__*/_jsxs("span", {
        ...{
          className: "x2lah0s xuxw1ft x141an7d xv1l7n4 x7a5moj"
        },
        children: ["+", items.length, " more"]
      }),
      children: tokens
    }) : tokens, /*#__PURE__*/_jsx(BaseTypeahead, {
      ref: inputRef,
      searchSource: isAtMax ? emptySource : filteredSource,
      value: null,
      onChange: handleAdd,
      renderItem: renderItem,
      placeholder: value.length === 0 ? placeholder : '',
      hasEntriesOnFocus: isAtMax ? false : hasEntriesOnFocus,
      maxMenuItems: maxMenuItems,
      emptySearchResultsText: emptySearchResultsText,
      isDisabled: isDisabled,
      isFocusableDisabled: showsDisabledMessage,
      hasAutoFocus: hasAutoFocus,
      inputId: inputId,
      ariaDescribedBy: ariaDescribedBy,
      onChangeQuery: onChangeQuery,
      debounceMs: debounceMs,
      onKeyDown: handleKeyDown,
      anchorRef: wrapperRef,
      size: size,
      inputXStyle: isAtMax || isTruncated ? styles.inputAtMax : value.length > 0 ? styles.inputCompact : undefined
    }), htmlName != null && value.map(item => /*#__PURE__*/_jsx("input", {
      type: "hidden",
      name: htmlName,
      value: item.id
      // Disabled native controls are excluded from form submission;
      // mirror that for the hidden carriers.
      ,
      disabled: isDisabled
    }, item.id)), (endContent || hasClear && value.length > 0 && !isDisabled) && /*#__PURE__*/_jsxs("div", {
      ...stylex.props(styles.endSection, endSectionSizeStyles[size]),
      children: [endContent, hasClear && value.length > 0 && !isDisabled && /*#__PURE__*/_jsx(InputClearButton, {
        label: t('@astryx.tokenizer.clearAll'),
        onClick: e => {
          e.stopPropagation();
          handleClearAll();
        }
      })]
    })]
  });
  let tokenizerContent;
  if (isLayerMode) {
    const placeholderSizeStyle = layerPlaceholderSizeStyles[size];
    tokenizerContent = /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("div", {
        ref: placeholderRef,
        onClick: handleWrapperClick,
        ...mergeProps(themeProps('tokenizer', {
          size,
          status: status?.type
        }), stylex.props(inputWrapperStyles.base, styles.wrapper, value.length > 0 && styles.wrapperWithTokens, placeholderSizeStyle, isTruncated && styles.truncatedWrapper, isDisabled && inputWrapperStyles.disabled, status && inputStatusBorderStyles[status.type], status && inputStatusHoverShadowStyles[status.type], status && inputStatusFocusWithinStyles[status.type])),
        children: isTruncated && /*#__PURE__*/_jsxs(_Fragment, {
          children: [startIcon && renderIconSlot(startIcon, {
            size: 'sm',
            color: 'secondary'
          }), /*#__PURE__*/_jsx(OverflowList, {
            gap: 1,
            behavior: "observeParent",
            overflowRenderer: items => /*#__PURE__*/_jsxs("span", {
              ...{
                className: "x2lah0s xuxw1ft x141an7d xv1l7n4 x7a5moj"
              },
              children: ["+", items.length, " more"]
            }),
            children: tokens
          })]
        })
      }), layer.render(/*#__PURE__*/_jsx("div", {
        ref: layerContentRef,
        onFocusCapture: handleFocusCapture,
        onBlurCapture: handleBlurCapture,
        children: wrapperContent
      }), {
        positioning: 'custom',
        xstyle: styles.layerPopover,
        style: popoverOverrideStyle
      })]
    });
  } else {
    tokenizerContent = wrapperContent;
  }
  return /*#__PURE__*/_jsxs(Field, {
    ref: ref,
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: inputId,
    descriptionID: description ? descriptionId : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageId : undefined
    } : undefined,
    labelTooltip: labelTooltip,
    width: width,
    xstyle: xstyle,
    className: className,
    style: style,
    children: [tokenizerContent, showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
Tokenizer.displayName = 'Tokenizer';